import { z } from 'zod';
import { uploadLinkConfigSchema } from './messaging.js';
import { VendorPaymentMethodType } from '../types/vendor.js';
import { isValidAbaRoutingNumber, normalizePayPalMeUsername, normalizeVenmoUsername, CASHTAG_PATTERN, normalizeCashtag, PAYPAL_ME_USERNAME_PATTERN, SWIFT_CODE_PATTERN, VENMO_USERNAME_PATTERN, } from '../utils/vendorPaymentMethods.js';
/**
 * Lenient URL validation: accepts common formats like "example.com", "www.example.com",
 * "http://example.com", "https://example.com". Normalizes by prepending https:// if missing protocol.
 */
const websiteSchema = z
    .string()
    .max(500)
    .refine((val) => {
    if (!val)
        return true;
    // If it has a protocol, validate as URL
    if (/^https?:\/\//i.test(val)) {
        try {
            new URL(val);
            return true;
        }
        catch {
            return false;
        }
    }
    // Otherwise, prepend https:// and try
    try {
        new URL(`https://${val}`);
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Invalid website URL' })
    .optional();
export const createVendorSchema = z.object({
    businessName: z.string().min(1).max(255),
    contactName: z.string().max(255).optional(),
    email: z.string().email().optional(),
    phone: z.string().max(50).optional(),
    address: z.string().max(500).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(100).optional(),
    zipCode: z.string().max(20).optional(),
    country: z.string().max(100).optional(),
    taxId: z.string().max(50).optional(),
    website: websiteSchema,
    notes: z.string().max(2000).optional(),
});
// Update fields are nullable: the edit panels send null to clear a field
export const updateVendorSchema = z.object({
    businessName: z.string().min(1).max(255).optional(),
    contactName: z.string().max(255).nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    zipCode: z.string().max(20).nullable().optional(),
    country: z.string().max(100).nullable().optional(),
    taxId: z.string().max(50).nullable().optional(),
    website: websiteSchema.nullable(),
    rating: z.number().min(0).max(5).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const deleteVendorSchema = z.object({
    reassignToVendorId: z.string().uuid().nullable().optional(),
});
export const addVendorCategorySchema = z.object({
    vendorCategoryId: z.string().uuid(),
    isPrimary: z.boolean().optional(),
});
/**
 * `POST /vendors/:id/contacts` — somebody already in the system (`userId`), or
 * a new person by `name` and `email`. A new person's account is a vendor
 * account that cannot sign in until the office turns their portal access on.
 */
export const addVendorContactSchema = z
    .object({
    userId: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(200).optional(),
    email: z.string().trim().toLowerCase().email().max(320).optional(),
    role: z.string().max(100).optional(),
    isPrimary: z.boolean().optional(),
    ccOnEmails: z.boolean().optional(),
})
    .refine((body) => body.userId !== undefined
    ? body.name === undefined && body.email === undefined
    : body.name !== undefined && body.email !== undefined, { message: 'Choose a person, or give a new person’s name and email.' });
/** `PUT /vendors/:id/contacts/:userId/portal-access` — may this contact sign in to the vendor portal. */
export const vendorContactPortalAccessSchema = z
    .object({
    enabled: z.boolean(),
    /** Email them that they can sign in (on turning it on). */
    notify: z.boolean().optional(),
})
    .strict();
export const vendorEmailAttachmentSchema = z.object({
    s3Key: z.string().min(1).max(1024),
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(100),
});
export const sendVendorEmailSchema = z.object({
    vendorIds: z.array(z.string().uuid()).min(1).max(100),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1).max(200_000),
    attachments: z.array(vendorEmailAttachmentSchema).max(10).optional(),
    /** Files from the library, by id — resolved against what the caller may see. */
    fileIds: z.array(z.string().uuid()).max(50).optional(),
    /*
     * ⚠️ **These four were read by the controller and missing here**, and a field
     * a schema does not name is stripped from the body rather than refused — so
     * "send a copy to myself", the manually-typed recipients and the whole
     * file-upload link were silently inert on every send. Nothing failed; the
     * values simply never arrived.
     */
    ccSelf: z.boolean().optional(),
    ccVendorContacts: z.boolean().optional(),
    additionalEmails: z.array(z.string().email().max(320)).max(50).optional(),
    includeUploadLink: z.boolean().optional(),
    uploadRequest: uploadLinkConfigSchema.optional(),
    asConversation: z.boolean().optional(),
});
/**
 * What a vendor may upload from the portal as a quote or invoice: the office
 * reads these, so documents, spreadsheets, PDFs and pictures of paper ones.
 * Keys from the shared `UPLOAD_FILE_TYPES` registry.
 */
export const VENDOR_DOCUMENT_FILE_TYPES = ['PDF', 'IMAGE', 'DOCUMENT', 'SPREADSHEET'];
/** `POST /portal/vendor/events/:eventId/documents/upload-url` */
export const vendorDocumentUploadUrlSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(1).max(255),
});
/**
 * `POST /portal/vendor/events/:eventId/documents` — finalize an upload the
 * presign issued. The expense is required: a document is only ever read from
 * the expense it belongs to, so one filed under none would be seen by nobody.
 */
export const vendorDocumentCreateSchema = z.object({
    s3Key: z.string().trim().min(1).max(1024),
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(1).max(255),
    documentType: z.enum(['quote', 'invoice']),
    expenseItemId: z.string().uuid(),
});
// ─── Vendor Payment Methods ─────────────────────────────────────────────────
/** An optional text field: trimmed, and blank read as none. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
/** Digits only, ignoring the spaces and dashes people type between them. */
const digits = (value) => value.replace(/[\s-]/g, '');
const optionalRoutingNumberSchema = z
    .string()
    .nullish()
    .transform((v) => (v ? digits(v) : null))
    .refine((v) => v === null || isValidAbaRoutingNumber(v), {
    message: 'Enter a valid 9-digit routing number',
});
const zelleDetailsSchema = z
    .object({
    recipientName: z.string().trim().min(1, 'Recipient name is required').max(255),
    email: z
        .string()
        .trim()
        .nullish()
        .transform((v) => (v ? v : null))
        .refine((v) => v === null || z.string().email().safeParse(v).success, {
        message: 'Enter a valid email',
    }),
    phone: optionalText(50),
})
    .refine((d) => !!d.email || !!d.phone, {
    message: 'Enter the email or phone enrolled with Zelle',
    path: ['email'],
});
const venmoDetailsSchema = z.object({
    username: z
        .string()
        .transform(normalizeVenmoUsername)
        .refine((v) => VENMO_USERNAME_PATTERN.test(v), {
        message: 'Enter a Venmo username (5–30 letters, numbers, - or _)',
    }),
    recipientName: optionalText(255),
    phoneLast4: z
        .string()
        .trim()
        .nullish()
        .transform((v) => (v ? v : null))
        .refine((v) => v === null || /^\d{4}$/.test(v), { message: 'Enter exactly 4 digits' }),
});
const wireAchDetailsSchema = z
    .object({
    accountHolderName: z.string().trim().min(1, 'Account holder is required').max(255),
    // Filled from the routing number (`lookupBankByRouting`); typed only for a bank the directory lacks.
    bankName: optionalText(255),
    // Either routing number will do — a vendor paid only by wire may not give the ACH one.
    routingNumber: optionalRoutingNumberSchema,
    accountNumber: z
        .string()
        .transform(digits)
        .refine((v) => /^\d{4,17}$/.test(v), { message: 'Enter a 4–17 digit account number' }),
    accountType: z.enum(['CHECKING', 'SAVINGS']),
    wireRoutingNumber: optionalRoutingNumberSchema,
    swiftCode: z
        .string()
        .trim()
        .nullish()
        .transform((v) => (v ? v.toUpperCase() : null))
        .refine((v) => v === null || SWIFT_CODE_PATTERN.test(v), {
        message: 'Enter an 8 or 11 character SWIFT/BIC code',
    }),
    bankAddress: optionalText(500),
    accountHolderAddress: optionalText(500),
})
    .refine((d) => !!d.routingNumber || !!d.wireRoutingNumber, {
    message: 'Enter the ACH or the wire routing number',
    path: ['routingNumber'],
});
const paypalDetailsSchema = z
    .object({
    paypalMeUsername: z
        .string()
        .nullish()
        .transform((v) => (v ? normalizePayPalMeUsername(v) : null))
        .transform((v) => (v ? v : null))
        .refine((v) => v === null || PAYPAL_ME_USERNAME_PATTERN.test(v), {
        message: 'Enter a PayPal.Me username (letters and numbers, up to 20)',
    }),
    email: z
        .string()
        .trim()
        .nullish()
        .transform((v) => (v ? v : null))
        .refine((v) => v === null || z.string().email().safeParse(v).success, {
        message: 'Enter a valid email',
    }),
})
    .refine((d) => !!d.paypalMeUsername || !!d.email, {
    message: 'Enter a PayPal.Me username or the PayPal email',
    path: ['paypalMeUsername'],
});
const cashAppDetailsSchema = z.object({
    cashtag: z
        .string()
        .transform(normalizeCashtag)
        .refine((v) => CASHTAG_PATTERN.test(v), {
        message: 'Enter a $Cashtag (up to 20 letters, numbers, - or _)',
    }),
    recipientName: optionalText(255),
});
/** A required text field — a blank sent as null reads with the same message as an empty one. */
const requiredText = (max, message) => z
    .string({ required_error: message, invalid_type_error: message })
    .trim()
    .min(1, message)
    .max(max);
/** No country, or the US — where a state and a ZIP code are part of every address. */
const isUsAddress = (country) => !country || /^(us|usa|u\.s\.a?\.?|united states( of america)?)$/i.test(country);
const checkDetailsSchema = z
    .object({
    payableTo: requiredText(255, 'Enter who the check is made out to'),
    addressLine1: requiredText(255, 'Enter the mailing address'),
    addressLine2: optionalText(255),
    city: requiredText(100, 'Enter the city'),
    // Required for a US address only: many countries have no state, and some no postal code.
    state: optionalText(100),
    zipCode: optionalText(20),
    country: optionalText(100),
})
    .superRefine((d, ctx) => {
    if (!isUsAddress(d.country))
        return;
    if (!d.state)
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'Enter the state' });
    if (!d.zipCode) {
        ctx.addIssue({ code: 'custom', path: ['zipCode'], message: 'Enter the ZIP code' });
    }
});
const paymentMethodCommon = {
    label: optionalText(100),
    isPreferred: z.boolean().optional(),
    notes: optionalText(1000),
};
/** `POST /vendors/:id/payment-methods` and `PUT /vendors/:id/payment-methods/:methodId`. */
export const vendorPaymentMethodSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(VendorPaymentMethodType.ZELLE),
        details: zelleDetailsSchema,
        ...paymentMethodCommon,
    }),
    z.object({
        type: z.literal(VendorPaymentMethodType.VENMO),
        details: venmoDetailsSchema,
        ...paymentMethodCommon,
    }),
    z.object({
        type: z.literal(VendorPaymentMethodType.WIRE_ACH),
        details: wireAchDetailsSchema,
        ...paymentMethodCommon,
    }),
    z.object({
        type: z.literal(VendorPaymentMethodType.PAYPAL),
        details: paypalDetailsSchema,
        ...paymentMethodCommon,
    }),
    z.object({
        type: z.literal(VendorPaymentMethodType.CASH_APP),
        details: cashAppDetailsSchema,
        ...paymentMethodCommon,
    }),
    z.object({
        type: z.literal(VendorPaymentMethodType.CHECK),
        details: checkDetailsSchema,
        ...paymentMethodCommon,
    }),
]);
//# sourceMappingURL=vendor.js.map